import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import HeaderStreakChip from "./HeaderStreakChip";
import { Beer, Truck, ShoppingBag, Video, MessageCircleQuestion, Crown, Layers, Music2, LogOut, Menu, X } from "lucide-react";

export default function Header({ onAuthClick }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);

  const links = [
    { to: "/", label: "Home", icon: Truck },
    { to: "/shop", label: "Shop", icon: ShoppingBag },
    { to: "/designs", label: "Designs", icon: Layers },
    { to: "/music", label: "Music", icon: Music2 },
    { to: "/videos", label: "Videos", icon: Video },
    { to: "/ask", label: "Ask a Redneck", icon: MessageCircleQuestion },
    { to: "/subscribe", label: "VIP", icon: Crown },
  ];

  const authedLinks = user ? [{ to: "/orders", label: "Orders", icon: ShoppingBag }] : [];

  return (
    <header className="bg-[#0A0E0B]/95 backdrop-blur-xl border-b-2 border-[#2C332A] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
          <div className="w-10 h-10 bg-[#DD5422] flex items-center justify-center">
            <Beer className="w-5 h-5 text-[#0A0E0B]" strokeWidth={3} />
          </div>
          <div className="leading-none">
            <div className="font-anton text-2xl md:text-3xl text-[#E5E5E5] uppercase tracking-tight">Everythang</div>
            <div className="font-anton text-xs text-[#DD5422] uppercase tracking-[0.3em]">Redneck</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {[...links, ...authedLinks].map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`px-4 py-2 font-anton uppercase tracking-widest text-sm transition-colors border-2 border-transparent
              ${loc.pathname === l.to ? "text-[#DD5422] border-[#DD5422]" : "text-[#E5E5E5] hover:text-[#DD5422]"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <HeaderStreakChip />
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-[#8D9688]">Howdy</div>
                <div className="font-anton text-base text-[#E5E5E5] uppercase flex items-center gap-1">
                  {user.name}
                  {user.is_subscriber && (
                    <span data-testid="vip-badge" className="ml-1 bg-[#DD5422] text-[#0A0E0B] px-1.5 py-0.5 text-[10px] tracking-widest">VIP</span>
                  )}
                </div>
              </div>
              <Button data-testid="logout-btn" onClick={logout} variant="outline"
                className="bg-transparent border-2 border-[#2C332A] hover:border-[#DD5422] text-[#E5E5E5] rounded-none font-anton uppercase tracking-widest">
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <Button data-testid="header-login-btn" onClick={() => onAuthClick("login")}
              className="bg-[#DD5422] text-[#0A0E0B] hover:bg-[#FF6B35] rounded-none font-anton uppercase tracking-widest border-2 border-[#DD5422]">
              Sign In
            </Button>
          )}
        </div>

        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="md:hidden p-2 border-2 border-[#2C332A]">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t-2 border-[#2C332A] bg-[#0A0E0B]" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {[...links, ...authedLinks].map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                className="block px-3 py-3 font-anton uppercase tracking-widest text-sm border-2 border-[#2C332A] hover:border-[#DD5422]">
                {l.label}
              </Link>
            ))}
            {user ? (
              <Button data-testid="mobile-logout-btn" onClick={() => { logout(); setOpen(false); }}
                className="w-full bg-[#2C332A] text-[#E5E5E5] rounded-none font-anton uppercase">Log Out ({user.name})</Button>
            ) : (
              <Button data-testid="mobile-login-btn" onClick={() => { onAuthClick("login"); setOpen(false); }}
                className="w-full bg-[#DD5422] text-[#0A0E0B] rounded-none font-anton uppercase">Sign In</Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
